import type {DiscussionTopic} from "@devplaninc/codeup"

export type ChatEvidenceProvider = "slack" | "teams"

export function chatProviderFromTopic(topic: DiscussionTopic | undefined): ChatEvidenceProvider | undefined {
  if (!topic) return undefined
  let firstProvider: ChatEvidenceProvider | undefined
  for (const ref of topic.sourceRefs) {
    if (ref.ref?.$case !== "slack" && ref.ref?.$case !== "teams") continue
    firstProvider ??= ref.ref.$case
    if (validHttpsUrl(ref.ref.value.permalink?.trim())) return ref.ref.$case
  }
  return firstProvider
}

export function chatHrefFromTopic(
  topic: DiscussionTopic | undefined,
  options: {nativeSlackDeepLinks?: boolean} = {},
): string | undefined {
  if (!topic) return undefined
  for (const ref of topic.sourceRefs) {
    if (ref.ref?.$case === "slack") {
      const source = ref.ref.value
      const href = slackHrefFromPermalink(source.permalink, {
        channelId: source.conversationId,
        messageTs: source.messageId || source.threadId,
        threadTs: source.threadId,
        nativeDeepLinks: options.nativeSlackDeepLinks,
      })
      if (href) return href
    }
    if (ref.ref?.$case === "teams") {
      const href = validHttpsUrl(ref.ref.value.permalink?.trim())
      if (href) return href
    }
  }
  return undefined
}

export function slackHrefFromTopic(
  topic: DiscussionTopic | undefined,
  options: {nativeDeepLinks?: boolean} = {},
): string | undefined {
  if (!topic) return undefined

  for (const ref of topic.sourceRefs) {
    if (ref.ref?.$case !== "slack") continue
    const source = ref.ref.value
    const href = slackHrefFromPermalink(source.permalink, {
      channelId: source.conversationId,
      messageTs: source.messageId || source.threadId,
      threadTs: source.threadId,
      nativeDeepLinks: options.nativeDeepLinks,
    })
    if (href) return href
  }

  return undefined
}

export function slackHrefFromPermalink(
  permalink: string | undefined,
  options: {teamId?: string; channelId?: string; messageTs?: string; threadTs?: string; nativeDeepLinks?: boolean} = {},
): string | undefined {
  const httpsPermalink = validHttpsUrl(permalink?.trim())
  if (options.nativeDeepLinks) {
    const nativeHref = slackNativeHref({
      teamId: options.teamId,
      channelId: options.channelId,
      messageTs: options.messageTs,
      threadTs: options.threadTs,
      permalink: httpsPermalink,
    })
    if (nativeHref) return nativeHref
  }
  return httpsPermalink
}

function slackNativeHref({
  teamId,
  channelId,
  messageTs,
  threadTs,
  permalink,
}: {
  teamId: string | undefined
  channelId: string | undefined
  messageTs: string | undefined
  threadTs: string | undefined
  permalink: string | undefined
}): string | undefined {
  const parsed = appPermalinkContext(permalink)
  const team = teamId?.trim() || parsed.teamId
  const channel = channelId?.trim() || parsed.channelId
  if (!team || !channel) return undefined
  const params = new URLSearchParams({team, id: channel})
  const normalizedMessageTs = normalizeSlackTs(messageTs) ?? parsed.messageTs
  if (normalizedMessageTs) params.set("message", normalizedMessageTs)
  const normalizedThreadTs = normalizeSlackTs(threadTs)
  if (normalizedThreadTs && normalizedThreadTs !== normalizedMessageTs) params.set("thread_ts", normalizedThreadTs)
  return `slack://channel?${params.toString()}`
}

function appPermalinkContext(raw: string | undefined): {teamId?: string; channelId?: string; messageTs?: string} {
  if (!raw) return {}
  try {
    const url = new URL(raw)
    const parts = url.pathname.split("/").filter(Boolean)
    if (url.hostname === "app.slack.com" && parts[0] === "client") {
      const channelId = parts[2]
      const threadPart = parts[4]
      const messageTs = threadPart?.startsWith(`${channelId}-`) ? threadPart.slice(channelId.length + 1) : undefined
      return {
        teamId: parts[1],
        channelId,
        messageTs: normalizeSlackTs(messageTs),
      }
    }
  } catch {
    return {}
  }
  return {}
}

function normalizeSlackTs(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  const withoutPrefix = trimmed.startsWith("p") ? trimmed.slice(1) : trimmed
  if (/^\d{10}\.\d{1,6}$/.test(withoutPrefix)) return withoutPrefix
  if (/^\d{16}$/.test(withoutPrefix)) return `${withoutPrefix.slice(0, 10)}.${withoutPrefix.slice(10)}`
  return trimmed
}

function validHttpsUrl(value: string | undefined): string | undefined {
  if (!value) return undefined
  try {
    const url = new URL(value)
    if (url.protocol === "https:") return value
  } catch {
    return undefined
  }
  return undefined
}
